import React from 'react';
import { motion } from 'framer-motion';
import { PageHeader } from '../components/PageHeader';
import { SectionLabel } from '../components/SectionLabel';
import { Bloom } from '../components/Bloom';
import { CTAButton } from '../components/CTAButton';
import { SEOHead } from '../components/SEOHead';
const reasons = [{
  title: 'You already pay for leads. This makes sure you don’t lose them.',
  body: 'Every call, text, and Yelp message gets answered, including the ones that come in mid-job.'
}, {
  title: 'One system, not fifty logins.',
  body: 'Leads, follow-up, invoices, and the calendar live in one place you actually check.'
}, {
  title: 'One team, not five vendors pointing fingers.',
  body: 'You stop being the middleman between the ad guy, the site guy, and the software guy.'
}, {
  title: 'We build the hard parts with you: AI receptionist, workflows, all in your voice.',
  body: 'Set up on a call, in your words. Not templates handed over as homework.'
}, {
  title: 'Works the same solo or with a crew.',
  body: 'One truck or five, it runs the same and grows when you do.'
}, {
  title: 'Month to month. We earn it every month.',
  body: 'No contracts. If it stops being worth it, you leave.'
}];
export function Why() {
  return <>
      <SEOHead title="Why Viridia Analytics | One System, One Team, No Missed Leads | Phoenix" description="Why Phoenix home-service businesses switch to Viridia: one system instead of fifty logins, one team instead of five vendors, and no more missed leads." canonical="/why" data-id="element-3621" data-mp="37yxu4mp4fzth" />

      <PageHeader label="Why Viridia" title="Why Viridia Analytics" intro="Six reasons owners switch, all of them about the jobs you book, not about us." data-id="element-3622" data-mp="2k3bt7encm26w" />

      <section className="relative isolate overflow-hidden border-b border-[#1a1a1a] bg-[#0c0c0c]" data-id="element-3623" data-mp="ibqgvd811h71">
        <Bloom size="md" className="left-0 top-[-6rem]" data-id="element-3624" data-mp="n8p7vmwe9810" />
        <div className="mx-auto w-full max-w-6xl px-6 py-20 lg:py-24" data-id="element-3625" data-mp="lsl6mk51ic3r">
          <h2 className="sr-only" data-id="element-3626" data-mp="ltw6i8vd6zpl">Reasons to work with Viridia Analytics</h2>
          <ul className="grid grid-cols-1 gap-x-12 gap-y-10 md:grid-cols-2" data-id="element-3627" data-mp="gsqgwjx03ska">
            {reasons.map((r, i) => <motion.li key={r.title} initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.4,
            delay: i * 0.05
          }} className="border-t border-[#1c1c1c] pt-6" data-id="element-3628" data-mp="buovhsc3rh5n" data-mpi={i}>
                <h3 className="text-lg font-semibold leading-snug text-white" data-id="element-3629" data-mp="usri60h6h20a">{r.title}</h3>
                <p className="mt-2.5 max-w-md text-sm leading-relaxed text-neutral-400" data-id="element-3630" data-mp="qs7iap57zkag">
                  {r.body}
                </p>
              </motion.li>)}
          </ul>

          <div className="mt-16 flex flex-col items-start gap-6 rounded-2xl border border-[#1c1c1c] bg-[#0d0d0d] p-7 sm:flex-row sm:items-center" data-id="element-3631" data-mp="1ij3wpu6xhwzu">
            <div className="rounded-xl bg-white p-3" data-id="element-3632" data-mp="3176ff7saaj1c">
              <img src="https://cdn.magicpatterns.com/uploads/it67T9N3RYnBZ32NVuR62H/SEOcertified.png" alt="HubSpot SEO certified badge held by the Viridia Analytics team" className="h-14 w-auto object-contain" data-id="element-3633" data-mp="364f3fp045hai" />
            </div>
            <div className="rounded-xl bg-white p-3" data-id="element-3634" data-mp="3k4pryldyajsi">
              <img src="https://cdn.magicpatterns.com/uploads/8HQjTNAJm388sm9my7xsCp/Scotsscommerce.png" alt="Viridia Analytics is a Scottsdale Area Chamber of Commerce member" className="h-14 w-auto object-contain" data-id="element-3635" data-mp="3k3y642a5o0ff" />
            </div>
            <p className="max-w-sm text-sm leading-relaxed text-neutral-400" data-id="element-3636" data-mp="1pyrlgx522ys">
              HubSpot SEO certified and a Scottsdale Area Chamber of Commerce member.
            </p>
          </div>
        </div>
      </section>

      <section className="relative isolate overflow-hidden border-b border-[#1a1a1a]" data-id="element-3637" data-mp="bmbik71au2pu">
        <Bloom size="md" className="right-0 top-[-6rem]" data-id="element-3638" data-mp="3u5ic111stm83" />
        <div className="mx-auto w-full max-w-6xl px-6 py-20 lg:py-24" data-id="element-3639" data-mp="3p90a08xus1zg">
          <motion.div initial={{
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-60px'
        }} transition={{
          duration: 0.45
        }} data-id="element-3640" data-mp="7rh6r1kl4fqy">
            <SectionLabel data-id="element-3641" data-mp="3b7o0af9acwm0">Who&rsquo;s behind it</SectionLabel>
            <h2 className="mt-8 max-w-3xl text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl" data-id="element-3642" data-mp="369e1luocrdvn">
              Built by people who&rsquo;ve worked both sides of it
            </h2>
            <p className="mt-5 max-w-2xl text-lg leading-relaxed text-neutral-400" data-id="element-3643" data-mp="3g7k2c8yfadyx">
              Viridia didn&rsquo;t come out of a marketing agency. It came from doing the work,
              then learning how to sell it.
            </p>
          </motion.div>

          <div className="mt-12 grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-[#1c1c1c] bg-[#1c1c1c] md:grid-cols-2" data-id="element-3644" data-mp="vgtb9o4yvzmx">
            {[{
            title: 'In the field',
            body: 'Years on the tools: cabinet finishing and installation, shower remodels, pest control. Sixteen-hour shifts to hit a deadline, and the standard that a job only counts when it’s done right.'
          }, {
            title: 'Behind the campaigns',
            body: 'Then the other side: former Yelp reps who onboarded home-service owners and managed their ad campaigns directly. Over $2 million a month in spend, run for real, scalable growth.'
          }].map((c, i) => <motion.div key={c.title} initial={{
            opacity: 0,
            y: 12
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.45,
            delay: i * 0.06
          }} className="bg-[#0d0d0d] p-8" data-id="element-3645" data-mp="1h03mp05h5bt1" data-mpi={i}>
                <h3 className="font-mono text-xs uppercase tracking-[0.18em] text-emerald-500" data-id="element-3646" data-mp="2jcd55kvvqm8p">
                  {c.title}
                </h3>
                <p className="mt-4 text-base leading-relaxed text-neutral-400" data-id="element-3647" data-mp="2ac8bsenitodj">{c.body}</p>
              </motion.div>)}
          </div>

          <motion.p initial={{
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-60px'
        }} transition={{
          duration: 0.45,
          delay: 0.1
        }} className="mt-10 max-w-3xl border-l-2 border-emerald-500 pl-5 text-lg leading-relaxed text-neutral-300" data-id="element-3648" data-mp="25cc4o6jj1gtr">
            Almost no one has done both. That&rsquo;s why the system is built around how a
            home-service business actually runs, not how it looks from the outside.
          </motion.p>
        </div>
      </section>

      <section className="relative isolate overflow-hidden border-b border-[#1a1a1a]" data-id="element-3649" data-mp="1co6hld503fqr">
        <Bloom size="lg" className="left-1/2 top-[-16rem] -translate-x-1/2" data-id="element-3650" data-mp="12l17gp4j1ggp" />
        <div className="mx-auto w-full max-w-3xl px-6 py-24 text-center lg:py-28" data-id="element-3651" data-mp="17jm8gqrls467">
          <h2 className="text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl" data-id="element-3652" data-mp="17lp6ny2wt3m0">
            Worth fifteen minutes?
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-lg leading-relaxed text-neutral-400" data-id="element-3653" data-mp="1hi22b1i0lrje">
            We look at where your leads go today and tell you straight if this helps.
          </p>
          <div className="mt-9 flex justify-center" data-id="element-3654" data-mp="osldedtlssrx">
            <CTAButton to="/contact" data-id="element-3655" data-mp="otedivy24k13">Book a call</CTAButton>
          </div>
        </div>
      </section>
    </>;
}